import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  CATEGORIE, CLASSI, COEFF_R, IMPORTO_VARIAZIONE_5, IMPORTO_VARIAZIONE_10,
  calcolaRinnovo, calcolaRevisione, calcolaAumento, calcolaVariazioni, importoClasse,
} from '../utils/corrispettivo';

// Pagina condivisa tra admin e impresa: il tipo di calcolo arriva dall'URL.
const TIPI = {
  rinnovo: {
    titolo: 'Corrispettivo — Nuova attestazione / Rinnovo',
    info: 'Importo minimo dovuto per una nuova attestazione o per il rinnovo, in base alle categorie e alle classifiche richieste.',
  },
  revisione: {
    titolo: 'Corrispettivo — Revisione',
    info: 'Revisione dell\'attestazione: il corrispettivo è il 60% di quello di una nuova attestazione.',
  },
  aumento: {
    titolo: 'Corrispettivo — Aumento di classifica',
    info: 'Aumento della classifica in una categoria già posseduta: si calcola sulla differenza tra classe nuova e classe attuale.',
  },
  variazioni: {
    titolo: 'Corrispettivo — Variazioni a corrispettivo fisso',
    info: 'Variazioni dell\'attestazione con importo fisso (5% e 10% del minimo).',
  },
};

const euro = v => `€ ${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const numero = v => Number(v).toLocaleString('it-IT', { maximumFractionDigits: 2 });
const campo = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096] bg-white';

function Risultato({ esito, formula, perc }) {
  return (
    <div className="bg-white rounded-xl shadow p-5 space-y-4">
      <div>
        <p className="text-xs text-gray-400">Corrispettivo minimo{perc ? ` (${perc})` : ''} — IVA esclusa</p>
        <p className="text-3xl font-bold text-[#1A3A5C]">{euro(esito.totale)}</p>
      </div>
      {esito.alternativo != null && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 inline-block">
          <p className="text-xs text-blue-700">
            Calcolo alternativo — voce di importo più alto per intero, le altre al 50%
          </p>
          <p className="text-2xl font-bold text-[#1A3A5C]">{euro(esito.alternativo)}</p>
        </div>
      )}
      {esito.scontato != null && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 inline-block">
          <p className="text-xs text-green-700">Con sconto del 20%</p>
          <p className="text-2xl font-bold text-green-700">{euro(esito.scontato)}</p>
        </div>
      )}
      {formula && (
        <div className="text-xs text-gray-500 border-t border-gray-100 pt-3 space-y-1">
          <p>Formula: [C / 12.500 + (2 × N + 8) × 413,16] × 1,0413 × R</p>
          <p>C = {numero(formula.C)} € · N = {formula.N} · R = {String(COEFF_R).replace('.', ',')}</p>
        </div>
      )}
    </div>
  );
}

// ── Nuova attestazione / Rinnovo e Revisione: elenco di categorie con classifica ──
function TabCategorie({ calcola, perc }) {
  const [righe, setRighe] = useState([{ categoria: '', classe: '' }]);

  const usate = new Set(righe.map(r => r.categoria).filter(Boolean));
  const complete = righe.filter(r => r.categoria && r.classe);
  const incomplete = righe.length - complete.length;
  const esito = useMemo(() => (complete.length ? calcola(complete.map(r => r.classe)) : null), [righe]);

  const set = (i, k, v) => setRighe(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-5 space-y-3">
        {righe.map((r, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_200px_auto] gap-3 items-end">
            <div>
              {i === 0 && <label className="block text-xs font-medium text-gray-600 mb-1">Categoria</label>}
              <select value={r.categoria} onChange={e => set(i, 'categoria', e.target.value)} className={campo}>
                <option value="">Seleziona una categoria</option>
                {CATEGORIE.map(c => (
                  <option key={c} value={c} disabled={usate.has(c) && c !== r.categoria}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              {i === 0 && <label className="block text-xs font-medium text-gray-600 mb-1">Classifica</label>}
              <select value={r.classe} onChange={e => set(i, 'classe', e.target.value)} className={campo}>
                <option value="">Seleziona una classe</option>
                {CLASSI.map(c => (
                  <option key={c.value} value={c.value}>{c.label} — € {c.importo.toLocaleString('it-IT')}</option>
                ))}
              </select>
            </div>
            <button type="button" disabled={righe.length === 1}
              onClick={() => setRighe(rs => rs.filter((_, j) => j !== i))}
              className="text-sm text-red-500 hover:underline disabled:opacity-30 pb-2">
              Rimuovi
            </button>
          </div>
        ))}
        <div className="flex flex-wrap gap-4 pt-1">
          <button type="button" onClick={() => setRighe(rs => [...rs, { categoria: '', classe: '' }])}
            className="text-sm font-medium text-[#2E6096] hover:underline">
            + Aggiungi categoria
          </button>
          <button type="button" onClick={() => setRighe([{ categoria: '', classe: '' }])}
            className="text-sm text-gray-500 hover:underline">
            Azzera
          </button>
        </div>
      </div>

      {incomplete > 0 && complete.length > 0 && (
        <p className="text-xs text-orange-600">Le righe senza categoria o classifica non sono conteggiate.</p>
      )}
      {esito
        ? <Risultato esito={esito} formula={esito.formula} perc={perc} />
        : <p className="text-sm text-gray-400">Seleziona categoria e classifica per vedere il corrispettivo.</p>}
    </div>
  );
}

// ── Aumento di classifica ───────────────────────────────────────────────
function TabAumento() {
  const [categoria, setCategoria] = useState('');
  const [vecchia, setVecchia] = useState('');
  const [nuova, setNuova] = useState('');

  const importoVecchia = importoClasse(vecchia);
  const nuoveAmmesse = CLASSI.filter(c => importoVecchia == null || c.importo > importoVecchia);
  // se cambio la classe attuale e la nuova non è più valida, la tolgo
  useEffect(() => {
    if (nuova && importoVecchia != null && importoClasse(nuova) <= importoVecchia) setNuova('');
  }, [vecchia]);

  const esito = categoria && vecchia && nuova ? calcolaAumento(vecchia, nuova) : null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Categoria</label>
          <select value={categoria} onChange={e => setCategoria(e.target.value)} className={campo}>
            <option value="">Seleziona una categoria</option>
            {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Classifica attuale</label>
          <select value={vecchia} onChange={e => setVecchia(e.target.value)} className={campo}>
            <option value="">Seleziona una classe</option>
            {CLASSI.slice(0, -1).map(c => <option key={c.value} value={c.value}>{c.label} — € {c.importo.toLocaleString('it-IT')}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nuova classifica</label>
          <select value={nuova} onChange={e => setNuova(e.target.value)} className={campo}>
            <option value="">Seleziona una classe</option>
            {nuoveAmmesse.map(c => <option key={c.value} value={c.value}>{c.label} — € {c.importo.toLocaleString('it-IT')}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button type="button" onClick={() => { setCategoria(''); setVecchia(''); setNuova(''); }}
            className="text-sm text-gray-500 hover:underline pb-2">Azzera</button>
        </div>
      </div>
      {esito
        ? <Risultato esito={esito} formula={esito.formula} perc="1/3 della differenza" />
        : <p className="text-sm text-gray-400">Seleziona categoria, classifica attuale e nuova classifica.</p>}
    </div>
  );
}

// ── Variazioni a corrispettivo fisso ────────────────────────────────────
function TabVariazioni() {
  const vuoto = { denominazione: false, sede: false, soggetti: false, nSoggetti: 1, qualita: false };
  const [v, setV] = useState(vuoto);
  const set = (k, val) => setV(s => ({ ...s, [k]: val }));
  const qualcuna = v.denominazione || v.sede || v.soggetti || v.qualita;
  const esito = qualcuna ? calcolaVariazioni(v) : null;

  const voce = (chiave, testo, importo) => (
    <label className="flex items-start gap-3 py-2 cursor-pointer">
      <input type="checkbox" checked={v[chiave]} onChange={e => set(chiave, e.target.checked)}
        className="mt-1 rounded accent-[#1A3A5C]" />
      <span className="text-sm text-gray-700">{testo}
        <span className="block text-xs text-gray-400">{importo}</span>
      </span>
    </label>
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-5 divide-y divide-gray-100">
        {voce('denominazione', '5.1.1 — Variazione denominazione o ragione sociale', `Importo base € ${numero(IMPORTO_VARIAZIONE_5)} (5%)`)}
        {voce('sede', '5.1.2 — Variazione della sede', `Importo base € ${numero(IMPORTO_VARIAZIONE_5)} (5%)`)}
        <div className="py-2">
          {voce('soggetti', '5.1.3 — Variazione della rappresentanza legale e/o della direzione tecnica',
            `Importo base € ${numero(IMPORTO_VARIAZIONE_10)} (10%), da calcolare per ogni soggetto`)}
          {v.soggetti && (
            <div className="ml-8 mb-1 w-40">
              <label className="block text-xs font-medium text-gray-600 mb-1">N. di soggetti</label>
              <select value={v.nSoggetti} onChange={e => set('nSoggetti', parseInt(e.target.value, 10))} className={campo}>
                {Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
        </div>
        {voce('qualita', '5.3.1 / 5.3.2 — Variazione per acquisizione o rinnovo del certificato di qualità aziendale',
          `Importo base € ${numero(IMPORTO_VARIAZIONE_5)} (5%)`)}
      </div>

      <p className="text-xs text-gray-500">
        Per la trasformazione societaria senza trasferimento d'azienda (5.1.4) e per la variazione del direttore
        tecnico che ha consentito la qualificazione (5.2.1) contattare la sede.
      </p>

      <div className="flex gap-4">
        <button type="button" onClick={() => setV(vuoto)} className="text-sm text-gray-500 hover:underline">Azzera</button>
      </div>

      {esito
        ? <Risultato esito={esito} />
        : <p className="text-sm text-gray-400">Seleziona almeno una variazione.</p>}
    </div>
  );
}

export default function CalcoloCorrispettivo() {
  const { tipo } = useParams();
  const def = TIPI[tipo];
  if (!def) return <Navigate to="/" replace />;

  return (
    <div className="max-w-4xl">
      <h1 className="text-xl sm:text-2xl font-bold text-[#1A3A5C] mb-1">{def.titolo}</h1>
      <p className="text-sm text-gray-500 mb-5">{def.info}</p>
      {/* key: cambiando sottomenu il modulo riparte pulito */}
      {tipo === 'rinnovo' && <TabCategorie key={tipo} calcola={calcolaRinnovo} />}
      {tipo === 'revisione' && <TabCategorie key={tipo} calcola={calcolaRevisione} perc="60%" />}
      {tipo === 'aumento' && <TabAumento key={tipo} />}
      {tipo === 'variazioni' && <TabVariazioni key={tipo} />}
      <p className="text-xs text-gray-400 mt-6">
        Corrispettivo minimo previsto dal D.P.R. 207/2010, allegato C, parte I. Importi IVA esclusa e indicativi:
        la sede conferma l'importo definitivo.
      </p>
    </div>
  );
}
